import { NODE_TYPES } from '../constants/nodeTypes';
import { SocketDefinition } from '../types';

export type NodeDirection = 'ltr' | 'ttb' | 'rtl';

export const HEADER_H = 48;
export const SOCKET_ROW = 22;
export const SOCKET_COL = 32;
export const DESC_H = 60;   // extra height added when a node has a description
/** Room under the last socket so the BR resize corner stays clear. */
export const SOCKET_BOTTOM_PAD = 24;
/** Pull the add-socket ghost off the node edge so it does not sit on the last socket. */
export const GHOST_SOCKET_OUTSET = 14;

export const ENDPOINT_ROW = 16;

export function calcNodeHeight(nIn: number, nOut: number, endpointCount = 0): number {
  const rows = Math.max(nIn, nOut, 1);
  return HEADER_H + rows * SOCKET_ROW + SOCKET_BOTTOM_PAD + (endpointCount > 0 ? endpointCount * ENDPOINT_ROW + 8 : 0);
}

export function socketY(index: number): number {
  return HEADER_H + index * SOCKET_ROW + SOCKET_ROW / 2;
}

/** Next-slot Y for the add-socket ghost — one full row below the last socket of that side. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function ghostSocketY(index: number, _nodeHeight?: number): number {
  return socketY(Math.max(0, index));
}

export function ghostSocketX(side: 'left' | 'right', nodeWidth: number): number {
  return side === 'left' ? -GHOST_SOCKET_OUTSET : nodeWidth + GHOST_SOCKET_OUTSET;
}


/** Width/height for a decision diamond based on rotation (0 = horizontal, 90 = vertical). */
export function calcDecisionSize(rotation: number = 0): { width: number; height: number } {
  const isVertical = (Math.round(Math.abs(rotation)) % 180) === 90;
  return isVertical ? { width: 120, height: 160 } : { width: 160, height: 120 };
}

/**
 * Build the two sockets for a decision diamond.
 * Horizontal: input at left corner (0, h/2), output at right corner (w, h/2).
 * Vertical:   input at top corner  (w/2, 0), output at bottom corner (w/2, h).
 */
export function buildDecisionSockets(w: number, h: number, rotation: number = 0): SocketDefinition[] {
  const isVertical = (Math.round(Math.abs(rotation)) % 180) === 90;
  return [
    {
      id: 'in-0',
      type: 'input',
      dataType: 'data',
      position: isVertical ? { x: w / 2, y: 0 } : { x: 0, y: h / 2 },
      label: 'Input',
      order: 0,
      tickSide: isVertical ? 'up' : undefined,
    },
    {
      id: 'out-0',
      type: 'output',
      dataType: 'data',
      position: isVertical ? { x: w / 2, y: h } : { x: w, y: h / 2 },
      label: 'Output',
      order: 0,
      tickSide: isVertical ? 'down' : undefined,
    },
  ] as SocketDefinition[];
}

/** X position for horizontally-distributed sockets (TTB mode). */
function socketX(index: number, count: number, totalWidth: number): number {
  if (count <= 1) return totalWidth / 2;
  const pad = 28;
  const span = totalWidth - 2 * pad;
  return pad + (index / (count - 1)) * span;
}

export function buildSocketsFromType(
  type: string,
  nodeWidth: number,
  extraInputs: { name: string }[] = [],
  extraOutputs: { name: string }[] = [],
): SocketDefinition[] {
  const typeDef = (NODE_TYPES as Record<string, { inputs: readonly string[]; outputs: readonly string[] } | undefined>)[type]
    ?? NODE_TYPES.service;

  const allInputNames = [...typeDef.inputs, ...extraInputs.map(e => e.name)];
  const allOutputNames = [...typeDef.outputs, ...extraOutputs.map(e => e.name)];

  const sockets: SocketDefinition[] = [];

  allInputNames.forEach((name, i) => {
    sockets.push({
      id: `in-${i}`,
      type: 'input',
      dataType: 'data',
      position: { x: 0, y: socketY(i) },
      label: name,
      order: i,
    });
  });

  allOutputNames.forEach((name, i) => {
    sockets.push({
      id: `out-${i}`,
      type: 'output',
      dataType: 'data',
      position: { x: nodeWidth, y: socketY(i) },
      label: name,
      order: i,
    });
  });

  return sockets;
}

/**
 * Build sockets with position adjusted for a given direction.
 * LTR: inputs left, outputs right (default).
 * RTL: inputs right, outputs left.
 * TTB: inputs top, outputs bottom (spread horizontally).
 */
/**
 * Build 4 sockets for general-purpose shapes: left+top as inputs, right+bottom as outputs.
 * This gives shapes natural connection points on all four sides.
 */
export function buildFourSidedSockets(w: number, h: number): SocketDefinition[] {
  return [
    { id: 'in-0',  type: 'input',  dataType: 'data', position: { x: 0,     y: h / 2 }, label: '', order: 0, tickSide: 'left' },
    { id: 'in-1',  type: 'input',  dataType: 'data', position: { x: w / 2, y: 0     }, label: '', order: 1, tickSide: 'up' },
    { id: 'out-0', type: 'output', dataType: 'data', position: { x: w,     y: h / 2 }, label: '', order: 0, tickSide: 'right' },
    { id: 'out-1', type: 'output', dataType: 'data', position: { x: w / 2, y: h     }, label: '', order: 1, tickSide: 'down' },
  ] as SocketDefinition[];
}

/**
 * Build sockets for triangle (right-pointing, 0°). Left side = inputs, right tip = outputs.
 * All socket positions lie on the triangle boundary [0,0],[0,h],[w,h/2].
 */
export function buildTriangleSockets(w: number, h: number): SocketDefinition[] {
  return [
    { id: 'in-0',  type: 'input',  dataType: 'data', position: { x: 0,       y: h / 2       }, label: '', order: 0, tickSide: 'left' as const },
    { id: 'in-1',  type: 'input',  dataType: 'data', position: { x: w / 2,   y: h / 4       }, label: '', order: 1, tickSide: 'up' as const },
    { id: 'out-0', type: 'output', dataType: 'data', position: { x: w,       y: h / 2       }, label: '', order: 0, tickSide: 'right' as const },
    { id: 'out-1', type: 'output', dataType: 'data', position: { x: w / 2,   y: 3 * h / 4   }, label: '', order: 1, tickSide: 'down' as const },
  ] as SocketDefinition[];
}

/**
 * Build sockets for triangle at an arbitrary rotation (0/90/180/270 degrees).
 * Each orientation places sockets on the correct boundary of the rotated triangle.
 */
export function buildTriangleSocketsRotated(w: number, h: number, rotation: number): SocketDefinition[] {
  const rot = ((Math.round(rotation) % 360) + 360) % 360;

  if (rot === 90) {
    // Down-pointing: vertices [0,0],[w,0],[w/2,h]
    // Inputs = top edge; outputs = bottom tip + right hyp midpoint
    return [
      { id: 'in-0',  type: 'input',  dataType: 'data', position: { x: w * 0.25,  y: 0         }, label: '', order: 0, tickSide: 'up' as const },
      { id: 'in-1',  type: 'input',  dataType: 'data', position: { x: w * 0.75,  y: 0         }, label: '', order: 1, tickSide: 'up' as const },
      { id: 'out-0', type: 'output', dataType: 'data', position: { x: w / 2,     y: h         }, label: '', order: 0, tickSide: 'down' as const },
      { id: 'out-1', type: 'output', dataType: 'data', position: { x: w * 0.25,  y: h * 0.5   }, label: '', order: 1 },
    ] as SocketDefinition[];
  }

  if (rot === 180) {
    // Left-pointing: vertices [w,0],[w,h],[0,h/2]
    // Inputs = right edge; outputs = left tip + hypotenuse midpoints
    return [
      { id: 'in-0',  type: 'input',  dataType: 'data', position: { x: w,       y: h / 2     }, label: '', order: 0, tickSide: 'right' as const },
      { id: 'in-1',  type: 'input',  dataType: 'data', position: { x: w / 2,   y: h / 4     }, label: '', order: 1, tickSide: 'up' as const },
      { id: 'out-0', type: 'output', dataType: 'data', position: { x: 0,       y: h / 2     }, label: '', order: 0, tickSide: 'left' as const },
      { id: 'out-1', type: 'output', dataType: 'data', position: { x: w / 2,   y: 3 * h / 4 }, label: '', order: 1, tickSide: 'down' as const },
    ] as SocketDefinition[];
  }

  if (rot === 270) {
    // Up-pointing: vertices [0,h],[w,h],[w/2,0]
    // Inputs = bottom edge; outputs = top tip + left hyp midpoint
    return [
      { id: 'in-0',  type: 'input',  dataType: 'data', position: { x: w * 0.25,  y: h         }, label: '', order: 0, tickSide: 'down' as const },
      { id: 'in-1',  type: 'input',  dataType: 'data', position: { x: w * 0.75,  y: h         }, label: '', order: 1, tickSide: 'down' as const },
      { id: 'out-0', type: 'output', dataType: 'data', position: { x: w / 2,     y: 0         }, label: '', order: 0, tickSide: 'up' as const },
      { id: 'out-1', type: 'output', dataType: 'data', position: { x: w * 0.75,  y: h * 0.5   }, label: '', order: 1 },
    ] as SocketDefinition[];
  }

  // 0° = right-pointing (default)
  return buildTriangleSockets(w, h);
}

/**
 * Build sockets for hexagon at rotation 0 (landscape/flat-top) or 90 (portrait/pointy-top).
 */
export function buildHexagonSocketsRotated(w: number, h: number, rotation: number): SocketDefinition[] {
  const rot = ((Math.round(rotation) % 180) + 180) % 180;
  if (rot === 90) {
    // Pointy-top hexagon: vertices [w/2,0],[w,h*0.25],[w,h*0.75],[w/2,h],[0,h*0.75],[0,h*0.25]
    return [
      { id: 'in-0',  type: 'input',  dataType: 'data', position: { x: 0,     y: h / 2 }, label: '', order: 0, tickSide: 'left' as const },
      { id: 'in-1',  type: 'input',  dataType: 'data', position: { x: w / 2, y: 0     }, label: '', order: 1, tickSide: 'up' as const },
      { id: 'out-0', type: 'output', dataType: 'data', position: { x: w,     y: h / 2 }, label: '', order: 0, tickSide: 'right' as const },
      { id: 'out-1', type: 'output', dataType: 'data', position: { x: w / 2, y: h     }, label: '', order: 1, tickSide: 'down' as const },
    ] as SocketDefinition[];
  }
  // 0° = landscape flat-top
  return buildFourSidedSockets(w, h);
}

/**
 * Project a canvas point (px, py) to the nearest point on the boundary of a general shape.
 * Used for socket sliding. Returns the clamped position on the shape edge.
 */
export function projectToGeneralShapeBoundary(
  type: string,
  w: number,
  h: number,
  shapeRotation: number,
  px: number,
  py: number,
): { x: number; y: number } {
  const cx = w / 2;
  const cy = h / 2;

  if (type === 'oval') {
    // Project via angle from center to ellipse boundary
    const dx = px - cx || 0.001;
    const dy = py - cy || 0.001;
    const angle = Math.atan2(dy, dx);
    return { x: cx + (w / 2) * Math.cos(angle), y: cy + (h / 2) * Math.sin(angle) };
  }

  if (type === 'rounded' || type === 'cross') {
    // Treat as rectangle boundary (close enough for sliding)
    return projectToRectBoundary(w, h, px, py);
  }

  if (type === 'diamond') {
    const pts = [w / 2, 0, w, h / 2, w / 2, h, 0, h / 2];
    return projectToPolygonBoundary(pts, cx, cy, px, py);
  }

  if (type === 'hexagon') {
    const rot = ((Math.round(shapeRotation) % 180) + 180) % 180;
    let pts: number[];
    if (rot === 90) {
      const oy = h * 0.25;
      pts = [w / 2, 0, w, oy, w, h - oy, w / 2, h, 0, h - oy, 0, oy];
    } else {
      const ox = w * 0.25;
      pts = [ox, 0, w - ox, 0, w, h / 2, w - ox, h, ox, h, 0, h / 2];
    }
    return projectToPolygonBoundary(pts, cx, cy, px, py);
  }

  if (type === 'triangle') {
    const rot = ((Math.round(shapeRotation) % 360) + 360) % 360;
    let pts: number[];
    if (rot === 90)       pts = [0, 0, w, 0, w / 2, h];
    else if (rot === 180) pts = [w, 0, w, h, 0, h / 2];
    else if (rot === 270) pts = [0, h, w, h, w / 2, 0];
    else                  pts = [0, 0, 0, h, w, h / 2];
    return projectToPolygonBoundary(pts, cx, cy, px, py);
  }

  if (type === 'box') {
    return projectToRectBoundary(w, h, px, py);
  }

  // Fallback: rectangle
  return projectToRectBoundary(w, h, px, py);
}

export function socketsMatchFourSided(sockets: SocketDefinition[]): boolean {
  const ids = new Set(sockets.map(s => s.id));
  return ids.has('in-0') && ids.has('in-1') && ids.has('out-0') && ids.has('out-1');
}

export function scaleSocketsToSize(
  sockets: SocketDefinition[],
  oldW: number,
  oldH: number,
  newW: number,
  newH: number,
): SocketDefinition[] {
  const sx = oldW === 0 ? 1 : newW / oldW;
  const sy = oldH === 0 ? 1 : newH / oldH;
  return sockets.map(s => ({
    ...s,
    position: {
      x: Math.round(s.position.x * sx),
      y: Math.round(s.position.y * sy),
    },
  }));
}

/** Rebuild four-side / polygon sockets for a sketch shape, preserving labels. */
export function rebuildSketchSockets(
  type: string,
  w: number,
  h: number,
  rotation: number,
  oldSockets?: SocketDefinition[],
): SocketDefinition[] {
  let sockets: SocketDefinition[];
  if (type === 'triangle') sockets = buildTriangleSocketsRotated(w, h, rotation);
  else if (type === 'hexagon') sockets = buildHexagonSocketsRotated(w, h, rotation);
  else sockets = buildFourSidedSockets(w, h);

  if (!oldSockets) return sockets;
  return sockets.map(ns => {
    const old = oldSockets.find(os => os.id === ns.id);
    if (!old) return ns;
    const next = old.type !== ns.type ? { ...ns, type: old.type } : ns;
    return old.label ? { ...next, label: old.label } : next;
  });
}

function projectToRectBoundary(w: number, h: number, px: number, py: number): { x: number; y: number } {
  const cx = w / 2, cy = h / 2;
  const dx = (px - cx) || 0.001;
  const dy = (py - cy) || 0.001;
  const scaleX = (w / 2) / Math.abs(dx);
  const scaleY = (h / 2) / Math.abs(dy);
  const scale = Math.min(scaleX, scaleY);
  return {
    x: Math.round(Math.max(0, Math.min(w, cx + dx * scale))),
    y: Math.round(Math.max(0, Math.min(h, cy + dy * scale))),
  };
}

function projectToPolygonBoundary(
  pts: number[],
  cx: number,
  cy: number,
  px: number,
  py: number,
): { x: number; y: number } {
  const dx = px - cx || 0.001;
  const dy = py - cy || 0.001;
  const n = pts.length / 2;
  let best = { x: pts[0], y: pts[1] };
  let bestDist = Infinity;

  for (let i = 0; i < n; i++) {
    const ax = pts[i * 2], ay = pts[i * 2 + 1];
    const bx = pts[((i + 1) % n) * 2], by = pts[((i + 1) % n) * 2 + 1];
    const ex = bx - ax, ey = by - ay;
    const denom = dx * ey - dy * ex;
    if (Math.abs(denom) < 1e-10) continue;
    const t = ((ax - cx) * ey - (ay - cy) * ex) / denom;
    const s = ((ax - cx) * dy - (ay - cy) * dx) / denom;
    if (t > 0 && s >= 0 && s <= 1) {
      const ix = cx + t * dx, iy = cy + t * dy;
      const dist = (ix - px) * (ix - px) + (iy - py) * (iy - py);
      if (dist < bestDist) { bestDist = dist; best = { x: Math.round(ix), y: Math.round(iy) }; }
    }
  }
  return best;
}

export function buildSocketsForDirection(
  type: string,
  width: number,
  height: number,
  direction: NodeDirection,
  extraInputs: { name: string }[] = [],
  extraOutputs: { name: string }[] = [],
): SocketDefinition[] {
  if (direction === 'ltr') return buildSocketsFromType(type, width, extraInputs, extraOutputs);

  const typeDef = (NODE_TYPES as Record<string, { inputs: readonly string[]; outputs: readonly string[] } | undefined>)[type]
    ?? NODE_TYPES.service;

  const allInputNames = [...typeDef.inputs, ...extraInputs.map(e => e.name)];
  const allOutputNames = [...typeDef.outputs, ...extraOutputs.map(e => e.name)];

  const sockets: SocketDefinition[] = [];

  if (direction === 'rtl') {
    allInputNames.forEach((name, i) => {
      sockets.push({
        id: `in-${i}`, type: 'input', dataType: 'data',
        position: { x: width, y: socketY(i) },
        label: name, order: i,
        tickSide: 'right',
      });
    });
    allOutputNames.forEach((name, i) => {
      sockets.push({
        id: `out-${i}`, type: 'output', dataType: 'data',
        position: { x: 0, y: socketY(i) },
        label: name, order: i,
        tickSide: 'left',
      });
    });
  } else {
    // TTB: inputs at top edge, outputs at bottom edge
    allInputNames.forEach((name, i) => {
      sockets.push({
        id: `in-${i}`, type: 'input', dataType: 'data',
        position: { x: socketX(i, allInputNames.length, width), y: 0 },
        label: name, order: i,
        tickSide: 'up',
      });
    });
    allOutputNames.forEach((name, i) => {
      sockets.push({
        id: `out-${i}`, type: 'output', dataType: 'data',
        position: { x: socketX(i, allOutputNames.length, width), y: height },
        label: name, order: i,
        tickSide: 'down',
      });
    });
  }

  return sockets;
}
